export type EmeterPhaseMetric = 'power' | 'current';

/**
 * Read [A, B, C] live values from a 3EM device status.
 * Supports active power (W) or current (A), across Gen1 and Gen2+ status shapes.
 * Pure helper — no store imports.
 */
export const readDevicePhaseValues = (
	status: Record<string, unknown> | undefined,
	metric: EmeterPhaseMetric = 'power',
): number[] | null => {
	if (!status) return null;

	const emKey = Object.keys(status).find((k) => /^em:\d+$/.test(k));
	if (emKey) {
		const em = status[emKey] as Record<string, number> | undefined;
		if (metric === 'power') {
			if (em && (em.a_act_power != null || em.b_act_power != null || em.c_act_power != null)) {
				return [
					Number(em.a_act_power ?? 0),
					Number(em.b_act_power ?? 0),
					Number(em.c_act_power ?? 0),
				];
			}
		} else if (em && (em.a_current != null || em.b_current != null || em.c_current != null)) {
			return [Number(em.a_current ?? 0), Number(em.b_current ?? 0), Number(em.c_current ?? 0)];
		}
	}

	const emeters = (status as { emeters?: { power?: number; current?: number }[] }).emeters;
	if (Array.isArray(emeters) && emeters.length >= 3) {
		return emeters
			.slice(0, 3)
			.map((e) => Number((metric === 'power' ? e?.power : e?.current) ?? 0));
	}

	return null;
};

/** Read [A, B, C] active power (W) from a 3EM device status, across generations. */
export const readDevicePhasePowers = (
	status: Record<string, unknown> | undefined,
): number[] | null => readDevicePhaseValues(status, 'power');
